import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { BiArrowBack, BiCamera, BiPencil } from 'react-icons/bi';
import { PiSteeringWheelFill } from 'react-icons/pi';
import { toast } from 'react-toastify';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import siteConfig from '@config';
import Loading from '@components/Utils/Loading';
import DefaultErrorFallback from '@components/Fallbacks/DefaultErrorFallback';
import DefaultEmptyFallback from '@components/Fallbacks/DefaultEmptyFallback';
import { useGetUserDetailsQuery } from '../queries/getUserDetails.query.generated';
import { useSaveUserDetailsMutation } from '../queries/saveUserDetails.mutation.generated';
import { useResolveDriverUserQuery } from '../queries/resolveDriverUser.query.generated';
import { useFeaturePermission } from '@utils/featurePermissions';
import { FeatureName } from '@utils/featureNames';
import { authService } from '@features/auth/services/authService';
import UserCard from '../components/UserCard';
import UserProfileTab from '../components/UserProfileTab';
import UserFeaturesTab from '../components/UserFeaturesTab';
import UserCustomersTab from '../components/UserCustomersTab';
import UserDriverTab from '../components/UserDriverTab';
import UserAvailabilityTab from '../components/UserAvailabilityTab';
import { hasDriverRole } from '../utils/roleUtils';
import { canHaveDrivers } from '@utils/tiers';
import { useUserEditForm } from '../hooks/useUserEditForm';
import { readableRooftopLinks } from '../utils/customerScopeUtils';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

interface TabDef {
  key: string;
  label: string;
  icon?: React.ReactElement;
}

export default function UserDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams<{ id: string }>();
  const [tab, setTab] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Detect entry route
  const isDriverRoute = location.pathname.startsWith('/drivers/');

  // Permission checks for tab gating
  const hasUserManagement = useFeaturePermission(FeatureName.USER_MANAGEMENT);
  const hasDriverManagement = useFeaturePermission(FeatureName.DRIVER_MANAGEMENT);

  // Resolve driverId → userId when entered via /drivers/:id
  const { data: driverResolve, loading: resolving } = useResolveDriverUserQuery({
    variables: { driverId: Number(routeId) },
    skip: !isDriverRoute || !routeId,
  });

  const resolvedUserId = isDriverRoute
    ? driverResolve?.drivers?.[0]?.user_id
    : Number(routeId);

  const { loading, error, data, refetch } = useGetUserDetailsQuery({
    variables: { userId: resolvedUserId! },
    skip: !resolvedUserId,
  });
  const [updateUser] = useSaveUserDetailsMutation();

  const user = useMemo(() => {
    const viewed = data?.users?.[0];
    return viewed ? { ...viewed, users_usertocustomers: readableRooftopLinks(viewed.users_usertocustomers) } : null;
  }, [data]);

  // Edit form hook — called unconditionally (Rules of Hooks)
  const editForm = useUserEditForm(
    user ?? { id: 0, display_name: '', email: '', phone: '' } as any,
    refetch,
  );

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  useReloadGuard(editForm.hasChanges || editForm.saving || uploadingAvatar, 'Team member profile');

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    e.target.value = '';

    if (file.size > 5_000_000) {
      toast.error('Avatar must be under 5 MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('File must be an image');
      return;
    }

    setUploadingAvatar(true);
    try {
      const token = await authService.getAccessTokenWithAutoRefresh();
      if (!token) throw new Error('Not authenticated');

      const ext = file.name.split('.').pop() || 'png';
      const fileName = `user_${user.id}_avatar_${Date.now()}.${ext}`;

      const { data: presignData } = await axios.post<{ uploadURL: string }>(
        `${siteConfig.hopdriveEventsUrl}/.netlify/functions/app-get-s3-signed-url`,
        {
          bucketName: 'driver-photos.hopdrive.com',
          filePath: '',
          fileName,
          fileType: file.type,
          requestType: 'photos',
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      await axios.put(presignData.uploadURL, file, {
        headers: { 'Content-Type': file.type },
      });

      const avatarUrl = presignData.uploadURL.split('?')[0];
      await updateUser({
        variables: { userId: user.id, userObj: { avatar_url: avatarUrl } },
      });
      toast.success('Avatar updated');
      refetch();
    } catch (err: any) {
      console.error('[AvatarUpload] failed:', err);
      const message = err?.response?.data?.error || err?.message || 'Unknown error';
      toast.error(`Failed to upload avatar: ${message}`);
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Driver tab conditions
  const showDriverTab = useMemo(() => {
    if (!user) return false;
    const hasLiteMarket = user.users_usertocustomers.some(
      (utc: any) => canHaveDrivers(utc.customer),
    );
    return hasLiteMarket || !!user.driver;
  }, [user]);

  const hasDriverRecord = !!user?.driver;

  // Features tab only relevant for portal-role users
  const isPortalUser = user ? user.default_role.includes('dealer') : false;

  const driverIcon = <PiSteeringWheelFill size={14} />;

  // Build dynamic tab list based on permissions and user state
  const tabs = useMemo(() => {
    const t: TabDef[] = [
      { key: 'profile', label: 'Profile' },
    ];
    if (hasUserManagement && isPortalUser) {
      t.push({ key: 'features', label: 'Features' });
    }
    t.push({ key: 'markets', label: 'Rooftops' });
    if (showDriverTab && hasDriverManagement) {
      t.push({ key: 'driver', label: 'Driver', icon: driverIcon });
    }
    if (hasDriverRecord && hasDriverManagement) {
      t.push({ key: 'availability', label: 'Availability', icon: driverIcon });
    }
    return t;
  }, [showDriverTab, hasDriverRecord, isPortalUser, hasUserManagement, hasDriverManagement]);

  // Set default tab based on entry route or ?tab= query param (only on first load)
  const defaultTabSet = useRef(false);
  useEffect(() => {
    if (defaultTabSet.current || tabs.length === 0) return;

    // Check for ?tab= query param first
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    if (requestedTab) {
      const idx = tabs.findIndex(t => t.key === requestedTab);
      if (idx >= 0) {
        setTab(idx);
        defaultTabSet.current = true;
        return;
      }
    }

    if (isDriverRoute) {
      const driverIdx = tabs.findIndex(t => t.key === 'driver');
      if (driverIdx >= 0) {
        setTab(driverIdx);
        defaultTabSet.current = true;
      }
    } else {
      defaultTabSet.current = true;
    }
  }, [tabs, isDriverRoute, location.search]);

  const currentTabKey = tabs[tab]?.key ?? 'profile';
  const heroEditable = currentTabKey === 'profile';

  // Auto-cancel edit when leaving profile tab
  useEffect(() => {
    if (!heroEditable && editForm.editing) {
      editForm.handleCancel();
    }
  }, [heroEditable]);

  // Clamp tab index when tabs array shrinks
  useEffect(() => {
    if (tab >= tabs.length && tabs.length > 0) {
      setTab(tabs.length - 1);
    }
  }, [tabs.length, tab]);

  // Back navigation — go back to the list we came from
  const backPath = isDriverRoute ? '/drivers' : '/users';
  const backLabel = isDriverRoute ? 'Back to Drivers' : 'Back to Users';

  // Early returns AFTER all hooks
  if (loading || resolving) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Loading position="fixed" />
      </Container>
    );
  }

  if (error) {
    console.error('Error getting user details:', error);
    Sentry.captureException(error);
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <DefaultErrorFallback message="ERROR GETTING USER DETAILS" />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <DefaultEmptyFallback message="NO USER FOUND" />
      </Container>
    );
  }

  const isDriver = hasDriverRole((user as any).users_usertoroles);
  const hasAvatar = !!user.avatar_url;
  const avatarSize = 64;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {/* Back link */}
      <Button
        variant="text"
        startIcon={<BiArrowBack />}
        onClick={() => navigate(backPath)}
        sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}
      >
        {backLabel}
      </Button>

      {/* Hero */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: '12px',
          boxShadow: '0px 4px 12px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.04)',
          position: 'relative',
        }}
      >
        {heroEditable && !editForm.editing && (
          <IconButton
            size="small"
            onClick={() => editForm.setEditing(true)}
            sx={{ position: 'absolute', top: 12, right: 12 }}
          >
            <BiPencil size={16} />
          </IconButton>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleAvatarUpload}
        />

        {editForm.editing ? (
          /* ── Edit mode ── */
          <Box>
            <Box display="flex" gap={2.5}>
              {/* Avatar with upload */}
              <Box
                sx={{
                  position: 'relative',
                  width: avatarSize,
                  height: avatarSize,
                  flexShrink: 0,
                  cursor: 'pointer',
                  '&:hover .avatar-overlay': { opacity: 1 },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Avatar
                  src={user.avatar_url || undefined}
                  alt={user.display_name}
                  sx={{ width: avatarSize, height: avatarSize, fontSize: avatarSize * 0.4 }}
                >
                  {user.display_name?.[0]?.toUpperCase()}
                </Avatar>
                <Box
                  className="avatar-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    opacity: hasAvatar ? 0 : 1,
                    transition: 'opacity 100ms ease-in-out',
                  }}
                >
                  <BiCamera size={avatarSize * 0.4} color="#fff" />
                </Box>
              </Box>

              {/* Edit fields */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  label="Display Name"
                  value={editForm.displayName}
                  onChange={e => editForm.setDisplayName(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Email"
                  value={editForm.email}
                  onChange={e => editForm.setEmail(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Phone"
                  value={editForm.phone}
                  onChange={e => editForm.setPhone(e.target.value)}
                  fullWidth
                  size="small"
                  helperText={isDriver && !editForm.phone.trim() ? 'This user will not be able to use rideshare without a phone number.' : undefined}
                  FormHelperTextProps={isDriver && !editForm.phone.trim() ? { sx: { color: 'warning.main' } } : undefined}
                />
              </Box>
            </Box>

            {/* Save / Cancel */}
            <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
              <Button variant="outlined" size="small" onClick={editForm.handleCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={editForm.handleSave}
                disabled={editForm.saving}
              >
                {editForm.saving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          /* ── Read mode ── */
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <UserCard
                hero
                avatarUrl={user.avatar_url}
                displayName={user.display_name ?? ''}
                email={user.email ?? ''}
                phone={user.phone}
                role={user.default_role}
                isActive={user.active}
                isDriver={isDriver}
                driverStatus={user.driver?.status || undefined}
                onAvatarClick={() => fileInputRef.current?.click()}
              />
              {isDriver && !user.phone && (
                <Typography variant="caption" color="warning.main" sx={{ pl: 10, mt: 0.25, display: 'block' }}>
                  This user will not be able to use rideshare without a phone number.
                </Typography>
              )}
            </Box>
            <Chip
              label={user.active ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                bgcolor: user.active ? '#f3fbf4' : '#f1f2f3',
                color: user.active ? '#30b448' : '#5f666d',
                fontSize: 11,
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          {tabs.map(t => (
            <Tab
              key={t.key}
              label={t.label}
              icon={t.icon}
              iconPosition="start"
              sx={t.icon ? { minHeight: 48 } : undefined}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab panels */}
      {currentTabKey === 'profile' && <UserProfileTab user={user} refetch={refetch} />}
      {currentTabKey === 'features' && <UserFeaturesTab user={user} refetch={refetch} />}
      {currentTabKey === 'markets' && <UserCustomersTab user={user} refetch={refetch} />}
      {currentTabKey === 'driver' && <UserDriverTab user={user} refetch={refetch} />}
      {currentTabKey === 'availability' && user.driver && (
        <UserAvailabilityTab driver={user.driver as any} user={user} refetch={refetch} />
      )}
    </Container>
  );
}
